import React from 'react';
import { Box, Typography, Skeleton, useTheme, Tooltip as MuiTooltip } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, Info } from '@mui/icons-material';
import { ResponsiveContainer, AreaChart, Area, Tooltip } from 'recharts';
import { KpiComponentProps } from '../types/analytics.types';

export const KpiComponent: React.FC<KpiComponentProps> = ({
  title,
  value,
  prefix = '',
  suffix = '',
  format = 'number',
  loading = false,
  error,
  subtitle,
  trend,
  variance,
  yoy,
  sparklineData,
  tallHeight = false,
  cardCount = 3
}) => {
  const theme = useTheme();

  const formatValue = (val: number | string): string => {
    if (val === undefined || val === null) {
      return 'N/A';
    }

    if (typeof val === 'string') {
      return val;
    }

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(val);
      case 'percentage':
        return `${val.toFixed(2)}%`;
      case 'number':
      default:
        return val.toLocaleString();
    }
  };

  const getTrendIcon = () => {
    if (!trend) return null;
    
    const iconProps = { 
      fontSize: 'small' as const, 
      sx: { ml: 1 } 
    };

    switch (trend.direction) {
      case 'up':
        return <TrendingUp {...iconProps} sx={{ ...iconProps.sx, color: theme.palette.success.main }} />;
      case 'down':
        return <TrendingDown {...iconProps} sx={{ ...iconProps.sx, color: theme.palette.error.main }} />;
      case 'neutral':
      default:
        return <TrendingFlat {...iconProps} sx={{ ...iconProps.sx, color: theme.palette.grey[500] }} />;
    }
  };

  const getTrendColor = () => {
    if (!trend) return theme.palette.text.secondary;
    
    switch (trend.direction) {
      case 'up':
        return theme.palette.success.main;
      case 'down':
        return theme.palette.error.main;
      case 'neutral':
      default:
        return theme.palette.text.secondary;
    }
  };

  const cardHeight = tallHeight ? '12rem' : '10.5rem'; // 192px when tall, 168px normal - increased to accommodate 80px sparkline
  // Responsive font sizes based on card count (more cards = smaller text)
  // Scale font sizes based on card count: 3-4 cards = normal, 5-6 = smaller, 7+ = smallest
  const getFontSize = (baseMin: string, baseVw: string, baseMax: string) => {
    if (cardCount >= 7) {
      return `clamp(${baseMin}, calc(${baseVw} * 0.7), calc(${baseMax} * 0.75))`;
    } else if (cardCount >= 5) {
      return `clamp(${baseMin}, calc(${baseVw} * 0.85), calc(${baseMax} * 0.875))`;
    }
    return `clamp(${baseMin}, ${baseVw}, ${baseMax})`;
  };
  
  const titleFontSize = getFontSize('0.75rem', '1.75vw', '0.875rem'); // Reduced from 0.875/1rem
  const subtitleFontSize = getFontSize('0.7rem', '1.5vw', '0.8rem'); // Reduced
  // Value text scales more aggressively when zoomed in (smaller max values)
  const valueFontSize = getFontSize('1rem', '3vw', '1.375rem'); // Reduced from 1.25/1.625rem

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e4e4e7',
          borderRadius: '8px',
          padding: '1rem',
          height: cardHeight,
          minHeight: cardHeight,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Header skeleton */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Skeleton variant="text" width="40%" height={18} />
          <Skeleton variant="text" width="50px" height={18} />
        </Box>
        {/* Value row skeleton */}
        <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flex: 1 }}>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" width="140px" height={28} sx={{ borderRadius: '4px' }} />
        </Box>
        {/* Sparkline skeleton - increased height */}
        <Skeleton variant="rectangular" width="100%" height={80} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e4e4e7',
          borderRadius: '8px',
          padding: '1rem',
          height: cardHeight,
          minHeight: cardHeight,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
          <Typography 
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: 1,
            color: '#09090b',
            letterSpacing: '-0.4px',
          }}
          >
            {title}
          </Typography>
          <Typography 
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: '1.875rem',
            lineHeight: 1,
            color: '#ef4444',
          }}
          >
            Error
          </Typography>
          {error && (
            <Typography 
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              color: '#71717a',
            }}
            >
              {error}
            </Typography>
          )}
      </Box>
    );
  }

  // Small icon sparkline for header
  const SmallSparklineIcon: React.FC<{ 
    color?: string; 
    direction?: 'up' | 'down' | 'neutral';
  }> = ({ color = '#16a34a', direction = 'up' }) => {
    let points: { x: number; y: number }[];
    
    if (direction === 'up') {
      points = [
        { x: 0, y: 20 },
        { x: 8, y: 18 },
        { x: 16, y: 15 },
        { x: 24, y: 12 },
        { x: 32, y: 8 },
        { x: 40, y: 5 },
        { x: 48, y: 2 },
      ];
    } else if (direction === 'down') {
      points = [
        { x: 0, y: 2 },
        { x: 8, y: 5 },
        { x: 16, y: 8 },
        { x: 24, y: 12 },
        { x: 32, y: 15 },
        { x: 40, y: 18 },
        { x: 48, y: 20 },
      ];
    } else {
      points = [
        { x: 0, y: 10 },
        { x: 8, y: 10 },
        { x: 16, y: 10 },
        { x: 24, y: 10 },
        { x: 32, y: 10 },
        { x: 40, y: 10 },
        { x: 48, y: 10 },
      ];
    }
    
    const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    
    return (
      <svg width="48" height="20" viewBox="0 0 48 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d={pathData}
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  // Full-width bottom sparkline component using Recharts (like AnalyticsChart)
  // This prevents stretching issues by using ResponsiveContainer
  const FullWidthSparkline: React.FC<{ 
    color?: string; 
    direction?: 'up' | 'down' | 'neutral';
    dataPoints?: Array<{ value: number; date?: string }>; // Actual data values with dates for the selected time range
  }> = ({ color = '#ea580c', direction = 'up', dataPoints }) => {
    // Convert data points to Recharts format - use REAL data from sparklineData
    // The dataPoints already contain the primary metric values and formatted dates from TabbedAnalyticsDashboard
    let chartData: Array<{ value: number; date?: string; dateFormatted?: string }> = [];
    
    // Debug: Log data points to help diagnose issues
    if (process.env.NODE_ENV === 'development') {
      if (dataPoints && dataPoints.length > 0) {
        console.log(`[KpiComponent Sparkline] Using ${dataPoints.length} real data points:`, dataPoints.slice(0, 3));
      } else {
        console.warn(`[KpiComponent Sparkline] No real data points available, using mock data`);
      }
    }
    
    if (dataPoints && dataPoints.length > 0) {
      // Use actual data points - date field already contains formatted date from dateFormatted || date || timeKey
      chartData = dataPoints.map(d => ({
        value: d.value, // Real metric value from primaryMetric field
        date: d.date, // Already formatted date string from TabbedAnalyticsDashboard
        dateFormatted: d.date // Use same value for display
      }));
    } else {
      // No data available - render empty sparkline instead of placeholder data
      // This prevents misleading placeholder data from being displayed
      chartData = [];
    }
    
    // Custom tooltip component matching AnalyticsChart date formatting
    const CustomTooltip = ({ active, payload }: any) => {
      if (!active || !payload || !payload.length) return null;
      
      const data = payload[0].payload;
      const value = payload[0].value;
      
      // Date is already formatted from TabbedAnalyticsDashboard (dateFormatted || date || timeKey)
      const dateDisplay = data.dateFormatted || data.date || '';
      
      return (
        <Box
          sx={{
            backgroundColor: '#ffffff',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontFamily: "'Inter', sans-serif",
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            border: '1px solid #e4e4e7',
            pointerEvents: 'none',
          }}
        >
          {dateDisplay && (
            <Box sx={{ fontSize: '0.7rem', color: '#71717a', marginBottom: '4px' }}>
              {dateDisplay}
            </Box>
          )}
          <Box sx={{ fontWeight: 700, color: '#09090b' }}>
            {prefix}{formatValue(value)}{suffix}
          </Box>
        </Box>
      );
    };
    
    // If no data, render empty sparkline area instead of placeholder
    if (chartData.length === 0) {
      return (
        <Box sx={{ width: '100%', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#71717a', fontFamily: "'Inter', sans-serif" }}>
            No data available
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ width: '100%', height: '64px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={`sparkline-gradient-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                <stop offset="100%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              fill={`url(#sparkline-gradient-${color.replace('#', '')})`}
              strokeWidth={2.5}
              fillOpacity={1}
              dot={false}
              activeDot={{ r: 3, fill: '#ffffff', stroke: color, strokeWidth: 1 }}
              isAnimationActive={false}
            />
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '3 3', strokeOpacity: 0.3 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  // Calculate variance direction and color
  // Always calculate info, defaulting to 0 if variance doesn't exist
  const getVarianceInfo = () => {
    // Check if previousValue is 0 - if so, variance cannot be calculated
    const isPreviousValueZero = variance?.previousValue === 0;
    const varianceValue = isPreviousValueZero ? 0 : (variance?.value ?? 0);
    // Check if variance is 0.0 (neutral) - show dash like template
    const isVarianceZero = varianceValue === 0;
    const direction: 'up' | 'down' | 'neutral' = varianceValue > 0 ? 'up' : varianceValue < 0 ? 'down' : 'neutral';
    const color = direction === 'up' ? theme.palette.success.main : direction === 'down' ? theme.palette.error.main : theme.palette.grey[500];
    return { direction, color, value: varianceValue, isPreviousValueZero, isVarianceZero };
  };

  // Calculate YoY direction and color
  // Always calculate info, defaulting to 0 if yoy doesn't exist
  const getYoyInfo = () => {
    const yoyValue = yoy?.value ?? 0;
    const direction: 'up' | 'down' | 'neutral' = yoyValue > 0 ? 'up' : yoyValue < 0 ? 'down' : 'neutral';
    const color = direction === 'up' ? theme.palette.success.main : direction === 'down' ? theme.palette.error.main : theme.palette.grey[500];
    return { direction, color, value: yoyValue };
  };

  const varianceInfo = getVarianceInfo();
  const yoyInfo = getYoyInfo();

  // Helper function to get variance trendline icon
  const getVarianceIcon = () => {
    const iconProps = { 
      fontSize: 'small' as const, 
      sx: { mr: 0.5 } 
    };

    switch (varianceInfo.direction) {
      case 'up':
        return <TrendingUp {...iconProps} sx={{ ...iconProps.sx, color: varianceInfo.color }} />;
      case 'down':
        return <TrendingDown {...iconProps} sx={{ ...iconProps.sx, color: varianceInfo.color }} />;
      case 'neutral':
      default:
        return <TrendingFlat {...iconProps} sx={{ ...iconProps.sx, color: varianceInfo.color }} />;
    }
  };

  // Helper function to get YoY trendline icon
  const getYoyIcon = () => {
    const iconProps = { 
      sx: { 
        mr: 0, 
        fontSize: '0.75rem' 
      } 
    };

    switch (yoyInfo.direction) {
      case 'up':
        return <TrendingUp {...iconProps} sx={{ ...iconProps.sx, color: yoyInfo.color }} />;
      case 'down':
        return <TrendingDown {...iconProps} sx={{ ...iconProps.sx, color: yoyInfo.color }} />;
      case 'neutral':
      default:
        return <TrendingFlat {...iconProps} sx={{ ...iconProps.sx, color: yoyInfo.color }} />;
    }
  };

  // Calculate YoY absolute value
  const yoyAbsoluteValue = yoy ? (yoy.currentValue - yoy.yoyValue) : 0;
  
  // Format percentages
  const formatPercentage = (val: number): string => {
    const sign = val >= 0 ? '+' : '';
    return `${sign}${val.toFixed(1)}%`;
  };

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e4e4e7',
        borderRadius: '8px',
        padding: '1rem',
        height: cardHeight,
        minHeight: cardHeight,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.375rem',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Header Row: Title on left, Variance percentage on right */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography 
            sx={{ 
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              fontSize: titleFontSize,
              lineHeight: 1,
              color: '#09090b',
              letterSpacing: '-0.4px',
            }}
          >
            {title}
          </Typography>
          {/* Variance percentage with trendline icon */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!varianceInfo.isPreviousValueZero && !varianceInfo.isVarianceZero && getVarianceIcon()}
            <Typography 
              sx={{ 
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: titleFontSize,
                lineHeight: 1,
                color: (varianceInfo.isPreviousValueZero || varianceInfo.isVarianceZero) ? theme.palette.grey[500] : varianceInfo.color,
              }}
            >
              {(varianceInfo.isPreviousValueZero || varianceInfo.isVarianceZero) ? '—' : formatPercentage(varianceInfo.value)}
            </Typography>
          </Box>
        </Box>
        {/* Subtitle (location name) */}
        {subtitle && (
          <Typography 
            sx={{ 
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              fontSize: subtitleFontSize,
              lineHeight: 1.2,
              color: '#18181b',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Main Value Row: Large value on left, info icon on right */}
      <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flex: 1 }}>
        {/* Main Value */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            data-testid={`${title}-currency-amount`}
            sx={{ 
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700,
              fontSize: valueFontSize,
              lineHeight: 1,
              color: '#09090b',
              whiteSpace: 'nowrap',
            }}
          >
            {prefix}{formatValue(value)}{suffix}
          </Typography>
        </Box>

        {/* Info icon with tooltip */}
        <MuiTooltip 
          title="The mini-trend line and % compare the selected business-day range to the immediately preceding equivalent range (previous day or previous set of business days) across the selected locations."
          arrow
        >
          <Info 
            sx={{ 
              fontSize: '1rem',
              color: theme.palette.grey[600],
              cursor: 'help',
            }} 
          />
        </MuiTooltip>
      </Box>

      {/* Bottom Sparkline - Full width */}
      <Box sx={{ width: '100%', marginTop: 'auto' }}>
        <FullWidthSparkline 
          color="#ea580c"
          direction={varianceInfo.direction}
          dataPoints={sparklineData}
        />
      </Box>
    </Box>
  );
};

export default KpiComponent; 